/* eslint-disable camelcase, no-magic-numbers, require-jsdoc */
import getPageParentBySlug from 'app/utilities/get-page-parent-by-slug';
import getVideoId from 'get-video-id';
import SOCIAL_LINKS from 'config/social';
import {
    description as defaultDescription,
    ORGANIZATION_ADDRESS,
    ORGANIZATION_EMAIL,
    ORGANIZATION_PHONE,
    PARENT_ORGANIZATION,
    seoAlternateName,
    seoDomain,
    seoImagePath,
    siteTitle
} from 'config/seo';

const LANGUAGE = 'en-NZ';
const LOGO_ID = `${seoDomain}/#logo`;
const ORG_ID = `${seoDomain}/#organization`;
const ROOT_URL = `${seoDomain}/`;
const WEBSITE_ID = `${seoDomain}/#website`;
const NEWS_URL = `${seoDomain}/news`;

const VENUE_SAME_AS = {
    'aotea-centre': 'https://www.wikidata.org/wiki/Q4778787',
    'auckland-art-gallery': 'https://www.wikidata.org/wiki/Q4819492',
    'auckland-town-hall': 'https://www.wikidata.org/wiki/Q1391825',
    'auckland-zoo': 'https://www.wikidata.org/wiki/Q2665285',
    'bruce-mason-centre': 'https://www.wikidata.org/wiki/Q108097277',
    'go-media-stadium': 'https://www.wikidata.org/wiki/Q1426996',
    'motat': 'https://www.wikidata.org/wiki/Q1954383',
    'new-zealand-maritime-museum': 'https://www.wikidata.org/wiki/Q7015520',
    'north-harbour-stadium': 'https://www.wikidata.org/wiki/Q3374817',
    'q-theatre': 'https://www.wikidata.org/wiki/Q7265267',
    'shed-10': 'https://www.wikidata.org/wiki/Q124713889',
    'the-civic': 'https://www.wikidata.org/wiki/Q4391509',
    'the-cloud': 'https://www.wikidata.org/wiki/Q7723487',
    'viaduct-events-centre': 'https://www.wikidata.org/wiki/Q7924448'
};

const SOCIAL_URLS = [
    SOCIAL_LINKS.facebook,
    SOCIAL_LINKS.instagram,
    SOCIAL_LINKS.linkedin,
    SOCIAL_LINKS.youtube
].filter(Boolean);

export function getCanonicalUrl(path = '') {
    const slug = String(path || '').replace(/^\/+/, '').replace(/\/+$/, '');

    return slug ? `${seoDomain}/${slug}` : ROOT_URL;
}

export function absoluteUrl(value) {
    if (!value) return undefined;
    if (Array.isArray(value)) return value.map((item) => absoluteUrl(item)).filter(Boolean);
    if (typeof value !== 'string') return value;
    if ((/^https?:\/\//i).test(value)) return value;
    if (value.startsWith('//')) return `https:${value}`;

    return value.startsWith('/') ? `${seoDomain}${value}` : `${seoDomain}/${value}`;
}

export function stripHtml(value = '') {
    return decodeEntities(String(value)
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim());
}

export function getPageCanonicalUrl(page = {}) {
    const attributes = entityAttributes(page);
    const type = page.type;
    const { parent, slug } = attributes;

    if (!slug || slug === 'home') return ROOT_URL;

    if (isEntityType(type, 'venues', 'venue')) return getCanonicalUrl(`venues/${slug}`);
    if (isEntityType(type, 'rooms', 'room')) return getCanonicalUrl(`rooms/${slug}`);
    if (type === 'news') return getCanonicalUrl(`news/${slug}`);

    const parentSlug = resolveParentSlug(parent);
    if (parentSlug) return getCanonicalUrl(`${parentSlug}/${slug}`);

    return getCanonicalUrl(slug);
}

export function getWebsiteStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: seoDomain,
        name: siteTitle,
        alternateName: seoAlternateName,
        description: defaultDescription,
        inLanguage: LANGUAGE,
        publisher: organizationRef()
    };
}

export function getGlobalStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@graph': [
            organization()
        ]
    };
}

export function getPageStructuredData({
    description,
    image,
    isNewsArticle,
    page,
    title,
    url
} = {}) {
    const attributes = entityAttributes(page);
    const pageUrl = url || getPageCanonicalUrl(page);
    const pageTitle = title || attributes.seo_title || attributes.name || siteTitle;
    const pageType = pageWebPageType(attributes.slug, page && page.type);
    const breadcrumbs = getBreadcrumbItems(page);
    const primaryImage = absoluteUrl(image || primaryImageFromAttributes(attributes));
    const isAboutOrContact = pageType === 'AboutPage' || pageType === 'ContactPage';

    return graph([
        webPageNode({
            type: pageType,
            url: pageUrl,
            name: pageTitle,
            description: pageDescription(attributes, description),
            mainEntity: isAboutOrContact ? organizationRef() : undefined,
            breadcrumb: breadcrumbs.length > 0,
            primaryImage
        }),
        primaryImage && primaryImageObject(`${pageUrl}#primaryimage`, primaryImage),
        (isNewsArticle || page && page.type === 'news') && getNewsArticleStructuredData(page, {
            description,
            image: primaryImage,
            title: pageTitle,
            url: pageUrl
        })
    ]);
}

export function getNewsCollectionStructuredData(url = NEWS_URL) {
    return withContext({
        '@type': 'CollectionPage',
        '@id': `${url}#webpage`,
        url,
        name: 'Auckland Conventions News',
        inLanguage: LANGUAGE
    });
}

export function getNewsArticleStructuredData(page, fallback = {}) {
    const attributes = entityAttributes(page);
    const url = fallback.url || getCanonicalUrl(`news/${attributes.slug || ''}`);
    const headline = attributes.name || fallback.title;
    if (!headline) return null;

    return compact({
        '@type': 'NewsArticle',
        '@id': `${url}#article`,
        mainEntityOfPage: idRef(`${url}#webpage`),
        url,
        headline,
        description: pageDescription(attributes, fallback.description),
        image: images(primaryImageFromAttributes(attributes) || fallback.image),
        datePublished: attributes.published_at || attributes.publish_at || attributes.created_at,
        dateModified: attributes.updated_at || attributes.modified_at || attributes.published_at || attributes.publish_at,
        author: attributes.author && {
            '@type': 'Person',
            name: stripHtml(attributes.author)
        },
        publisher: organizationRef(),
        articleSection: 'News',
        inLanguage: LANGUAGE,
        isAccessibleForFree: true,
        isPartOf: idRef(`${NEWS_URL}#webpage`)
    });
}

export function getVenueStructuredData(venue = {}, page = {}) {
    const attributes = entityAttributes(venue);
    if (!attributes.name) return null;

    const url = getCanonicalUrl(`venues/${attributes.slug}`);
    const image = absoluteUrl(primaryImageFromAttributes(attributes));
    const rooms = Array.isArray(attributes.capacity) ? attributes.capacity : [];
    const pageUrl = page.url || url;
    const sameAs = VENUE_SAME_AS[attributes.slug];

    return graph([
        webPageNode({
            url: pageUrl,
            name: attributes.seo_title || attributes.name,
            description: pageDescription(attributes),
            mainEntity: `${url}#venue`,
            primaryImage: image
        }),
        compact({
            '@type': 'EventVenue',
            '@id': `${url}#venue`,
            name: attributes.name,
            alternateName: attributes.alternate_name,
            description: entityDescription(attributes),
            url,
            mainEntityOfPage: idRef(`${pageUrl}#webpage`),
            image: image && [idRef(`${pageUrl}#primaryimage`)],
            address: postalAddress(attributes),
            geo: geoCoordinates(attributes),
            telephone: attributes.telephone || attributes.phone || ORGANIZATION_PHONE,
            hasMap: attributes.place_id && `https://www.google.com/maps/place/?q=place_id:${attributes.place_id}`,
            maximumAttendeeCapacity: maximumCapacity(publishableRooms(rooms), attributes),
            amenityFeature: amenityFeatures(attributes),
            containsPlace: publishableRooms(rooms)
                .map((room) => idRef(`${seoDomain}/rooms/${room.slug}#room`)),
            subjectOf: subjectOfEntities(attributes, url),
            sameAs: sameAs && [sameAs]
        }),
        image && primaryImageObject(`${pageUrl}#primaryimage`, image)
    ]);
}

export function getRoomStructuredData(room = {}, page = {}) {
    const attributes = entityAttributes(room);
    if (!attributes.name) return null;

    const url = getCanonicalUrl(`rooms/${attributes.slug}`);
    const image = absoluteUrl(primaryImageFromAttributes(attributes));
    const pageUrl = page.url || url;
    const capacities = roomCapacities(attributes.event_types);
    const venueSlug = relatedVenueSlug(attributes);

    return graph([
        webPageNode({
            url: pageUrl,
            name: attributes.seo_title || attributes.name,
            description: pageDescription(attributes),
            mainEntity: `${url}#room`,
            primaryImage: image
        }),
        compact({
            '@type': 'Room',
            '@id': `${url}#room`,
            name: attributes.name,
            description: entityDescription(attributes),
            url,
            image: image && [idRef(`${pageUrl}#primaryimage`)],
            containedInPlace: venueSlug && idRef(`${seoDomain}/venues/${venueSlug}#venue`),
            floorSize: attributes.square_meters && {
                '@type': 'QuantitativeValue',
                value: Number(attributes.square_meters),
                unitCode: 'MTK',
                unitText: 'square metres'
            },
            maximumAttendeeCapacity: Math.max(...Object.values(capacities).filter(Boolean), 0) || undefined,
            amenityFeature: amenityFeatures(attributes),
            additionalProperty: capacityProperties(capacities, attributes.height)
        }),
        image && primaryImageObject(`${pageUrl}#primaryimage`, image)
    ]);
}

export function getHomepageStructuredData({ description, image, title } = {}) {
    const pageUrl = ROOT_URL;
    const primaryImage = absoluteUrl(image);

    return graph([
        webPageNode({
            url: pageUrl,
            name: title || siteTitle,
            description: stripHtml(description || defaultDescription),
            mainEntity: organizationRef(),
            breadcrumb: false,
            primaryImage
        }),
        primaryImage && primaryImageObject(`${pageUrl}#primaryimage`, primaryImage)
    ]);
}

export function getEventTypeLandingStructuredData(page = {}, options = {}) {
    const attributes = entityAttributes(page);
    const url = options.url || getPageCanonicalUrl(page);
    const name = options.title || attributes.seo_title || attributes.name;
    const description = pageDescription(attributes, options.description);
    if (!name || !url) return null;

    return graph([
        webPageNode({
            url,
            name,
            description,
            breadcrumb: true
        })
    ]);
}

export function getPlanningServiceStructuredData(page = {}, options = {}) {
    const attributes = entityAttributes(page);
    const url = options.url || getPageCanonicalUrl(page);
    const name = options.title || attributes.seo_title || attributes.name;
    const description = pageDescription(attributes, options.description);
    if (!name || !url) return null;

    return graph([
        webPageNode({
            url,
            name,
            description,
            mainEntity: `${url}#service`
        }),
        compact({
            '@type': 'Service',
            '@id': `${url}#service`,
            name,
            description,
            serviceType: attributes.service_type || name,
            provider: organizationRef(),
            areaServed: {
                '@type': 'City',
                name: 'Auckland'
            },
            availableChannel: {
                '@type': 'ServiceChannel',
                serviceUrl: absoluteUrl(attributes.enquiry_url || options.enquiryUrl || '/contact'),
                servicePhone: {
                    '@type': 'ContactPoint',
                    telephone: attributes.telephone || ORGANIZATION_PHONE
                }
            }
        })
    ]);
}

export function getCaseStudyStructuredData(page = {}, options = {}) {
    const attributes = entityAttributes(page);
    const url = options.url || getPageCanonicalUrl(page);
    const headline = options.title || attributes.name || attributes.seo_title;
    if (!headline || !url) return null;

    const image = absoluteUrl(options.image || primaryImageFromAttributes(attributes));
    const description = pageDescription(attributes, options.description);
    const venueSlug = relatedVenueSlug(attributes);
    const venueId = venueSlug && `${seoDomain}/venues/${venueSlug}#venue`;
    const breadcrumbs = getBreadcrumbItems(asPage(page, attributes));

    return graph([
        webPageNode({
            url,
            name: attributes.seo_title || headline,
            description,
            mainEntity: `${url}#article`,
            breadcrumb: breadcrumbs.length > 0,
            primaryImage: image
        }),
        compact({
            '@type': 'Article',
            '@id': `${url}#article`,
            mainEntityOfPage: idRef(`${url}#webpage`),
            headline,
            description,
            image: image && [idRef(`${url}#primaryimage`)],
            datePublished: attributes.published_at || attributes.publish_at || attributes.created_at,
            dateModified: attributes.updated_at || attributes.modified_at || attributes.published_at || attributes.publish_at,
            author: {
                '@type': attributes.author_type || 'Organization',
                name: attributes.author || seoAlternateName
            },
            publisher: organizationRef(),
            about: (attributes.event_name || attributes.event_start_date) && {
                '@type': 'Event',
                '@id': `${url}#event`,
                name: attributes.event_name || headline,
                startDate: attributes.event_start_date,
                endDate: attributes.event_end_date,
                location: venueId && idRef(venueId),
                organizer: attributes.event_organizer_name && {
                    '@type': 'Organization',
                    name: attributes.event_organizer_name
                }
            },
            contentLocation: venueId && idRef(venueId),
            inLanguage: LANGUAGE
        }),
        image && primaryImageObject(`${url}#primaryimage`, image)
    ]);
}

export function getPersonProfileStructuredData(page = {}, options = {}) {
    const attributes = entityAttributes(page);
    const url = options.url || getPageCanonicalUrl(page);
    const name = options.title || attributes.name || attributes.seo_title;
    if (!name || !url) return null;

    const image = absoluteUrl(options.image || attributes.thumbnail || primaryImageFromAttributes(attributes));
    const knowsAbout = [].concat(attributes.knows_about || attributes.expertise || []).filter(Boolean);
    const sameAs = [].concat(attributes.same_as || attributes.linkedin_url || []).filter(Boolean);
    const breadcrumbs = getBreadcrumbItems(asPage(page, attributes));

    return graph([
        webPageNode({
            type: 'ProfilePage',
            url,
            name: attributes.seo_title || name,
            description: pageDescription(attributes, options.description),
            mainEntity: `${url}#person`,
            breadcrumb: breadcrumbs.length > 0
        }),
        compact({
            '@type': 'Person',
            '@id': `${url}#person`,
            name,
            jobTitle: attributes.job_title || attributes.title,
            description: entityDescription(attributes),
            image,
            worksFor: organizationRef(),
            knowsAbout: knowsAbout.length ? knowsAbout : undefined,
            sameAs: sameAs.length ? sameAs : undefined,
            mainEntityOfPage: idRef(`${url}#webpage`)
        })
    ]);
}

export function getReviewStructuredData({ id, itemReviewedId, authorName, authorType = 'Person', reviewBody, datePublished, url } = {}) {
    if (!reviewBody || !authorName || !itemReviewedId) return null;

    const pageUrl = absoluteUrl(url) || ROOT_URL;

    return compact({
        '@type': 'Review',
        '@id': `${pageUrl}#review-${id || slugify(authorName)}`,
        itemReviewed: idRef(itemReviewedId),
        author: {
            '@type': authorType,
            name: stripHtml(authorName)
        },
        reviewBody: stripHtml(reviewBody),
        datePublished
    });
}

export function getCertificationStructuredData({
    name,
    certificationId,
    issuedBy,
    datePublished,
    expires,
    url
} = {}) {
    if (!name || !url) return null;

    return compact({
        '@type': 'Certification',
        '@id': `${absoluteUrl(url)}#certification`,
        name,
        certificationIdentification: certificationId,
        issuedBy: issuedBy && {
            '@type': 'Organization',
            name: issuedBy
        },
        datePublished,
        expires,
        url: absoluteUrl(url)
    });
}

export function getStructuredDataForPage(page = {}, options = {}) {
    const attributes = entityAttributes(page);
    const parentSlug = resolveParentSlug(attributes.parent);
    const pageUrl = options.url || getPageCanonicalUrl(page);

    if (attributes.slug === 'home') {
        return null;
    }

    if (isEntityType(page.type, 'venues', 'venue')) {
        return getVenueStructuredData(attributes, { url: pageUrl });
    }

    if (isEntityType(page.type, 'rooms', 'room')) {
        return getRoomStructuredData(attributes, { url: pageUrl });
    }

    if (parentSlug === 'event-types') {
        return getEventTypeLandingStructuredData(page, options);
    }

    if (parentSlug === 'services') {
        return getPlanningServiceStructuredData(page, options);
    }

    if (parentSlug === 'case-studies') {
        return getCaseStudyStructuredData(page, options);
    }

    if (isPersonProfilePage(attributes, parentSlug)) {
        return getPersonProfileStructuredData(page, options);
    }

    return getPageStructuredData({
        ...options,
        page,
        url: pageUrl
    });
}

export function getContentBlocksStructuredData(blocks = [], page = {}) {
    const url = absoluteUrl(page.url) || ROOT_URL;
    const relatedEntityId = page.relatedEntityId;
    const nodes = [];
    const faqQuestions = [];

    (Array.isArray(blocks) ? blocks : []).forEach((block, index) => {
        if (block.type === 'faq') {
            faqQuestions.push(...faqQuestionsFromBlock(block));

            return;
        }
        if (block.type === 'video') {
            nodes.push(video(block, index, url, page.title));

            return;
        }
        if (block.type === 'file') {
            nodes.push(digitalDocument(block, index, url));

            return;
        }
        if (block.type === 'pagebreakquote' || block.type === 'testimonial' || block.type === 'review') {
            const data = block.data || {};
            nodes.push(getReviewStructuredData({
                id: index + 1,
                itemReviewedId: relatedEntityId,
                authorName: data.citation || data.author || data.name,
                reviewBody: data.quote || data.content || data.reviewBody,
                datePublished: data.date || data.datePublished,
                url
            }));
        }
    });

    if (faqQuestions.length) {
        nodes.unshift(compact({
            '@type': 'FAQPage',
            '@id': `${url}#faq`,
            url,
            name: page.title,
            inLanguage: LANGUAGE,
            mainEntity: faqQuestions
        }));
    }

    return graph(nodes);
}

export function getBreadcrumbStructuredData(page) {
    const pageUrl = getPageCanonicalUrl(page);
    const items = getBreadcrumbItems(page);
    if (!items.length) return null;

    return withContext({
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: items
    });
}

function getBreadcrumbItems(page = {}) {
    const attributes = entityAttributes(page);
    const type = page.type;
    const { slug, name } = attributes;
    const parentSlug = resolveParentSlug(attributes.parent);

    if (!slug || slug === 'home') return [];

    const items = [listItem(1, 'Home', ROOT_URL)];

    if (isEntityType(type, 'rooms', 'room')) {
        return items.concat(
            listItem(2, 'Rooms', getCanonicalUrl('rooms')),
            listItem(3, name, getCanonicalUrl(`rooms/${slug}`))
        );
    }

    if (isEntityType(type, 'venues', 'venue')) {
        return items.concat(
            listItem(2, 'Venues', getCanonicalUrl('venues')),
            listItem(3, name, getCanonicalUrl(`venues/${slug}`))
        );
    }

    if (type === 'news') {
        return items.concat(
            listItem(2, 'News', NEWS_URL),
            listItem(3, name, getCanonicalUrl(`news/${slug}`))
        );
    }

    if (parentSlug === 'case-studies' || slug.startsWith('case-study')) {
        return items.concat(
            listItem(2, 'Case studies', getCanonicalUrl('case-studies')),
            listItem(3, name, getPageCanonicalUrl(asPage(page, attributes)))
        );
    }

    if (parentSlug === 'our-people' || slug.startsWith('staff-') || isPersonProfilePage(attributes, parentSlug)) {
        return items.concat(
            listItem(2, 'Our people', getCanonicalUrl('about-us/our-people')),
            listItem(3, name, getPageCanonicalUrl(asPage(page, attributes)))
        );
    }

    if (parentSlug) {
        const { slug: resolvedParentSlug, name: parentName } = getPageParentBySlug(parentSlug);

        return items.concat(
            listItem(2, parentName, getCanonicalUrl(resolvedParentSlug)),
            listItem(3, name, getCanonicalUrl(`${resolvedParentSlug}/${slug}`))
        );
    }

    return [];
}


function organization() {
    return {
        '@type': 'Organization',
        '@id': ORG_ID,
        name: seoAlternateName,
        alternateName: 'ACVE',
        url: seoDomain,
        logo: logoImageObject(),
        description: defaultDescription,
        telephone: ORGANIZATION_PHONE,
        email: ORGANIZATION_EMAIL,
        address: ORGANIZATION_ADDRESS,
        contactPoint: [
            {
                '@type': 'ContactPoint',
                contactType: 'venue enquiries',
                telephone: ORGANIZATION_PHONE,
                email: ORGANIZATION_EMAIL,
                areaServed: 'NZ',
                availableLanguage: ['en']
            }
        ],
        parentOrganization: PARENT_ORGANIZATION,
        areaServed: {
            '@type': 'City',
            name: 'Tāmaki Makaurau Auckland'
        },
        sameAs: SOCIAL_URLS
    };
}

function organizationRef() {
    return idRef(ORG_ID);
}

function idRef(id) {
    return id ? { '@id': id } : undefined;
}

function entityAttributes(entity = {}) {
    return entity.attributes || entity;
}

function asPage(page = {}, attributes = entityAttributes(page)) {
    return page.attributes ? page : { type: page.type || 'pages', attributes };
}

function isEntityType(type, ...names) {
    return names.includes(type);
}

function publishableRooms(rooms = []) {
    return (Array.isArray(rooms) ? rooms : []).filter((room) => {
        if (!room || !room.slug) return false;

        const slug = String(room.slug).toLowerCase();
        const name = String(room.name || '').toLowerCase();

        // Drop security-probe / junk CMS rows from venue containsPlace.
        if ((/cmsapi|vulnerability|javascript:|%3c|<|>|\$\{/).test(slug)) return false;
        if ((/cmsapi|vulnerability/).test(name)) return false;

        return true;
    });
}

function pageDescription(attributes = {}, fallback) {
    return stripHtml(
        fallback ||
        attributes.seo_description ||
        attributes.short_description ||
        attributes.description
    );
}

function entityDescription(attributes = {}) {
    return stripHtml(
        attributes.short_description ||
        attributes.seo_description ||
        attributes.description ||
        attributes.bio
    );
}

function relatedVenueSlug(attributes = {}) {
    return attributes.venue_slug ||
        attributes.venue && (attributes.venue.slug || attributes.venue.attributes && attributes.venue.attributes.slug);
}

function webPageNode({
    type = 'WebPage',
    url,
    name,
    description,
    mainEntity,
    breadcrumb = true,
    primaryImage
} = {}) {
    return compact({
        '@type': type,
        '@id': `${url}#webpage`,
        url,
        name,
        description,
        mainEntity: typeof mainEntity === 'string' ? idRef(mainEntity) : mainEntity,
        breadcrumb: breadcrumb ? idRef(`${url}#breadcrumb`) : undefined,
        primaryImageOfPage: primaryImage && idRef(`${url}#primaryimage`),
        inLanguage: LANGUAGE
    });
}

function listItem(position, name, item) {
    return {
        '@type': 'ListItem',
        position,
        name,
        item
    };
}

function logoImageObject() {
    return {
        '@type': 'ImageObject',
        '@id': LOGO_ID,
        url: seoImagePath
    };
}

function primaryImageObject(id, url) {
    if (!url) return null;

    return {
        '@type': 'ImageObject',
        '@id': id,
        url,
        representativeOfPage: true
    };
}

function pageWebPageType(slug, type) {
    if (slug === 'about-us' || slug === 'about') return 'AboutPage';
    if (slug === 'contact' || slug === 'contact-us') return 'ContactPage';
    if (type === 'news' && slug === 'news') return 'CollectionPage';

    return 'WebPage';
}

function primaryImageFromAttributes(attributes = {}) {
    return attributes.main_images && attributes.main_images[0] || attributes.thumbnail;
}

function postalAddress(attributes = {}) {
    if (!attributes.address && !attributes.street_address) return undefined;

    return compact({
        '@type': 'PostalAddress',
        streetAddress: attributes.street_address || attributes.address,
        addressLocality: attributes.address_locality || 'Auckland',
        addressRegion: attributes.address_region || 'Auckland',
        postalCode: attributes.postal_code,
        addressCountry: 'NZ'
    });
}

function geoCoordinates(attributes = {}) {
    const latitude = attributes.latitude || attributes.geo_lat;
    const longitude = attributes.longitude || attributes.geo_long;
    if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
        return undefined;
    }

    return {
        '@type': 'GeoCoordinates',
        latitude: Number(latitude),
        longitude: Number(longitude)
    };
}

function maximumCapacity(rooms = [], attributes = {}) {
    if (attributes.maximum_attendee_capacity) return Number(attributes.maximum_attendee_capacity);

    const values = rooms.reduce((capacities, room) => {
        const roomCaps = roomCapacities(room.event_types || []);

        return capacities.concat(Object.values(roomCaps).filter(Boolean));
    }, []);

    return values.length ? Math.max(...values) : undefined;
}

function roomCapacities(eventTypes = []) {
    const capacities = {};

    (Array.isArray(eventTypes) ? eventTypes : []).forEach(({ layout_types: layoutTypes = [] }) => {
        layoutTypes.forEach(({ name, capacity_max: capacityMax }) => {
            if (!name || capacityMax === undefined || capacityMax === null) return;
            capacities[name] = Math.max(capacities[name] || 0, Number(capacityMax));
        });
    });

    return capacities;
}

function capacityProperties(capacities = {}, height) {
    const labels = {
        Theatre: 'Theatre capacity',
        Classroom: 'Classroom capacity',
        Cabaret: 'Cabaret capacity',
        Banquet: 'Banquet capacity',
        Cocktail: 'Cocktail capacity',
        Boardroom: 'Boardroom capacity',
        'U-Shape': 'U-Shape capacity'
    };

    const properties = Object.keys(labels)
        .filter((key) => capacities[key])
        .map((key) => ({
            '@type': 'PropertyValue',
            name: labels[key],
            value: capacities[key]
        }));

    if (height) {
        properties.push({
            '@type': 'PropertyValue',
            name: 'Ceiling height',
            value: Number(height),
            unitText: 'metres'
        });
    }

    return properties;
}

function faqQuestionsFromBlock(block) {
    return (block.data || [])
        .filter((item) => item.title && item.content)
        .map((item) => ({
            '@type': 'Question',
            name: stripHtml(item.title),
            acceptedAnswer: {
                '@type': 'Answer',
                text: stripHtml(item.content)
            }
        }));
}

function video(block, index, url, title) {
    const data = block.data || {};
    const parsed = getVideoId(data.video_url || '');
    const embedUrl = videoEmbedUrl(parsed);

    return data.title && embedUrl && compact({
        '@type': 'VideoObject',
        '@id': `${url}#video-${index + 1}`,
        name: data.title,
        description: stripHtml(data.description || data.credit || title),
        embedUrl,
        thumbnailUrl: parsed.service === 'youtube' ? `https://img.youtube.com/vi/${parsed.id}/hqdefault.jpg` : undefined,
        publisher: organizationRef(),
        isFamilyFriendly: true,
        inLanguage: LANGUAGE
    });
}

function digitalDocument(block, index, url) {
    const data = block.data || {};
    const contentUrl = absoluteUrl(data.file || data.url || data.content_url);
    if (!contentUrl || !data.title && !data.name) return null;

    return compact({
        '@type': 'DigitalDocument',
        '@id': `${contentUrl}#document`,
        name: data.title || data.name,
        description: stripHtml(data.description),
        contentUrl,
        encodingFormat: data.mime_type || data.encodingFormat,
        publisher: organizationRef(),
        inLanguage: LANGUAGE,
        about: idRef(`${url}#webpage`)
    });
}

function videoEmbedUrl(videoData) {
    if (!videoData || !videoData.id) return undefined;
    if (videoData.service === 'youtube') return `https://www.youtube.com/embed/${videoData.id}`;
    if (videoData.service === 'vimeo') return `https://player.vimeo.com/video/${videoData.id}`;

    return undefined;
}

function images(value) {
    const image = absoluteUrl(value);

    return Array.isArray(image) ? image : image && [image];
}

function resolveParentSlug(parent) {
    if (!parent) return '';

    return typeof parent === 'string' ? parent : parent.slug || '';
}

function isPersonProfilePage(attributes = {}, parentSlug = '') {
    if (attributes.job_title || attributes.knows_about || attributes.expertise) return true;

    return parentSlug === 'our-people';
}

function amenityFeatures(attributes = {}) {
    const amenities = attributes.amenities || attributes.amenity_feature || attributes.features;
    if (!Array.isArray(amenities) || !amenities.length) return undefined;

    return amenities.map((amenity) => {
        if (typeof amenity === 'string') {
            return {
                '@type': 'LocationFeatureSpecification',
                name: amenity,
                value: true
            };
        }

        return compact({
            '@type': 'LocationFeatureSpecification',
            name: amenity.name || amenity.title,
            value: amenity.value === undefined ? true : amenity.value
        });
    }).filter((amenity) => amenity && amenity.name);
}

function subjectOfEntities(attributes = {}, venueUrl) {
    const subjects = [];

    (attributes.blocks || []).forEach((block, index) => {
        if (block.type === 'video' && block.data && block.data.title) {
            subjects.push({ '@id': `${venueUrl}#video-${index + 1}` });
        }
        if (block.type === 'file') {
            const fileUrl = absoluteUrl(block.data && (block.data.file || block.data.url));
            if (fileUrl) subjects.push({ '@id': `${fileUrl}#document` });
        }
    });

    return subjects;
}

function slugify(value = '') {
    return String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'item';
}

function withContext(schema) {
    return compact({ '@context': 'https://schema.org', ...schema });
}

function graph(nodes) {
    // Filter falsy placeholders from `condition && node` before compacting.
    const data = compact((Array.isArray(nodes) ? nodes : []).filter(Boolean));

    return data && data.length ? { '@context': 'https://schema.org', '@graph': data } : null;
}

function compact(value) {
    if (Array.isArray(value)) return value.map((item) => compact(item)).filter((item) => !empty(item));

    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((result, key) => {
            const item = compact(value[key]);
            if (!empty(item)) result[key] = item;

            return result;
        }, {});
    }

    return value;
}

function empty(value) {
    return value === undefined ||
        value === null ||
        value === '' ||
        Array.isArray(value) && !value.length ||
        Boolean(value && typeof value === 'object' && !Array.isArray(value) && !Object.keys(value).length);
}

function decodeEntities(value) {
    return String(value).replace(
        /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/g,
        (match, entity) => {
            if (entity[0] === '#') {
                const codePoint = entity[1] === 'x' || entity[1] === 'X' ?
                    parseInt(entity.slice(2), 16) :
                    parseInt(entity.slice(1), 10);

                if (Number.isNaN(codePoint)) {
                    return match;
                }

                try {
                    return String.fromCodePoint(codePoint);
                } catch (error) {
                    return match;
                }
            }

            const named = {
                amp: '&',
                lt: '<',
                gt: '>',
                quot: '"',
                apos: '\'',
                nbsp: ' ',
                eacute: 'é',
                Eacute: 'É',
                rsquo: '\u2019',
                lsquo: '\u2018',
                rdquo: '\u201d',
                ldquo: '\u201c',
                mdash: '\u2014',
                ndash: '\u2013',
                hellip: '\u2026'
            };

            return Object.prototype.hasOwnProperty.call(named, entity) ? named[entity] : match;
        }
    );
}
